import { Linkified } from "./Linkified";
import { Download, Link, Pencil, X } from "lucide-react";
import type { ReactNode } from "react";
import { PEOPLE, type PersonId } from "../data/people";
import { notify } from "../lib/notify";
import { taskItems } from "../lib/domain";
import { formatBytes, plural } from "../lib/utils";
import type { FileItem, SubTask, Task } from "../types";
import { montageApi } from "../api";
import { KindIcon } from "./FileDrop";
import { Button } from "./ui/button";
import { Avatar } from "./ui/primitives";

/** Общая раскладка модалки задачи: заголовок, разделы слева и карточка фактов справа */

export function ModalTitle({ title, onClose, onEdit }: { title: string; onClose: () => void; onEdit?: () => void }) {
  return (
    <header className="flex items-start gap-4">
      <h2 className="min-w-0 flex-1 text-[22px] font-semibold leading-snug tracking-[-0.02em]">{title}</h2>
      {onEdit && (
        <Button variant="ghost" size="icon" className="-mt-1 shrink-0" onClick={onEdit} title="Изменить публикацию: название, описание, аккаунт">
          <Pencil className="!size-[16px]" />
        </Button>
      )}
      <Button variant="ghost" size="icon" className="-mr-2 -mt-1 shrink-0" onClick={onClose} title="Закрыть (Esc)">
        <X className="!size-[18px]" />
      </Button>
    </header>
  );
}

/** Раздел модалки — все заголовки в одном стиле */
export function ModalSection({ title, count, right, children }: { title: string; count?: number; right?: ReactNode; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-2.5">
      <h3 className="flex h-6 items-center gap-1.5 text-[13px] font-semibold">
        {title}
        {!!count && <span className="font-medium text-muted-foreground">{count}</span>}
        {right && <span className="ml-auto font-normal">{right}</span>}
      </h3>
      {children}
    </section>
  );
}

/** Человек сверху карточки фактов: аватар, имя, под ними — что происходит с задачей и действия */
export function PersonStatus({ who, role, children, actions }: { who: PersonId; role: string; children?: ReactNode; actions?: ReactNode }) {
  return (
    <div className="flex flex-col gap-2.5 border-b border-black/[0.06] pb-3.5 pt-2">
      <div className="flex items-center gap-2.5">
        <Avatar id={who} size={36} />
        <span className="flex min-w-0 flex-col">
          <span className="text-xs text-muted-foreground">{role}</span>
          <span className="truncate text-sm font-semibold">{PEOPLE[who].name}</span>
        </span>
      </div>
      {children && <div className="text-[13px] leading-snug">{children}</div>}
      {actions && <div className="flex flex-wrap gap-1.5">{actions}</div>}
    </div>
  );
}

/** Карточка фактов справа */
export function FactCard({ children }: { children: ReactNode }) {
  return <aside className="flex flex-col rounded-[20px] bg-muted/50 px-4 py-2">{children}</aside>;
}

export function Prop({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid min-h-10 grid-cols-[72px_minmax(0,1fr)] items-center gap-2 py-1.5 text-[13px]">
      <span className="text-muted-foreground">{label}</span>
      <span className="flex min-w-0 items-center gap-1.5 font-medium">{children}</span>
    </div>
  );
}

/** Скачивание исходников; ошибка — тостом, а не молчанием. */
async function downloadFiles(files: FileItem[]) {
  const remote = files.filter((f) => f.remote);
  if (remote.length === 0) return;
  notify(`Скачиваем ${remote.length === 1 ? remote[0].name : `${remote.length} ${plural(remote.length, ["файл", "файла", "файлов"])}`}`, { icon: "info" });
  try {
    await montageApi.download(remote.map((f) => ({ kind: "file" as const, id: f.id })));
  } catch (error) {
    notify((error as Error).message || "Не удалось скачать", { icon: "rework" });
  }
}

/** Файлы строками; монтажёру — с кнопкой скачать */
export function FileList({ files, download }: { files: FileItem[]; download?: boolean }) {
  return (
    <div className="flex flex-col gap-0.5">
      {files.map((f) => (
        <div key={f.id} className="flex h-9 items-center gap-2.5 text-[13px]">
          <span className="grid size-7 shrink-0 place-items-center rounded-[9px] bg-muted">
            <KindIcon kind={f.kind} className="size-3.5" />
          </span>
          <span className="min-w-0 flex-1 truncate font-medium">{f.name}</span>
          <span className="shrink-0 text-xs text-muted-foreground">{formatBytes(f.size)}</span>
          {download && (
            <Button variant="ghost" size="iconSm" className="-mr-2" title="Скачать" disabled={!f.remote} onClick={() => void downloadFiles([f])}>
              <Download />
            </Button>
          )}
        </div>
      ))}
    </div>
  );
}

function DownloadAll({ files }: { files: FileItem[] }) {
  return (
    <button
      type="button"
      onClick={() => void downloadFiles(files)}
      className="flex items-center gap-1 text-[13px] font-medium text-muted-foreground hover:text-foreground"
    >
      <Download className="size-3.5" />
      Скачать всё
    </button>
  );
}

/** Содержимое задачи: одна задача — бриф и исходники, список — пронумерованные пункты со своим описанием и файлами */
/** `actions` — кнопки у видео (удалить, снять); кому какие, решает экран. */
export function TaskItems({ task, briefTitle = "Бриф", download, actions }: { task: Task; briefTitle?: string; download?: boolean; actions?: (item: SubTask) => ReactNode }) {
  const items = taskItems(task);
  if (items.length === 1) {
    const it = items[0];
    return (
      <>
        <ModalSection title={items.length === 1 && actions ? it.title : briefTitle} right={actions?.(it)}>
          {it.brief && <p className="whitespace-pre-wrap text-sm leading-relaxed [overflow-wrap:anywhere]"><Linkified text={it.brief} /></p>}
          {it.reference && (
            <span className="flex min-w-0 items-center gap-2 text-[13px] text-muted-foreground">
              <Link className="size-3.5 shrink-0" />
              <span className="truncate underline underline-offset-2">{it.reference}</span>
            </span>
          )}
        </ModalSection>
        {it.files.length > 0 && (
          <ModalSection title="Исходники" count={it.files.length} right={download && it.files.length > 1 ? <DownloadAll files={it.files} /> : undefined}>
            <FileList files={it.files} download={download} />
          </ModalSection>
        )}
      </>
    );
  }
  const all = items.flatMap((i) => i.files);
  return (
    <ModalSection title="Видео" count={items.length} right={download && all.length > 1 ? <DownloadAll files={all} /> : undefined}>
      <ol className="flex flex-col gap-2">
        {items.map((it, i) => (
          <li key={it.id} className="flex flex-col gap-2 rounded-[18px] bg-muted/50 px-4 py-3.5">
            <div className="flex items-center gap-2.5">
              <span className="grid size-5 shrink-0 place-items-center rounded-full bg-foreground text-[11px] font-semibold text-background">{i + 1}</span>
              <span className="min-w-0 flex-1 truncate text-sm font-semibold">{it.title}</span>
              <span className="shrink-0 text-xs text-muted-foreground">{it.format}</span>
              {actions?.(it)}
            </div>
            {it.brief && <p className="whitespace-pre-wrap text-[13px] leading-relaxed [overflow-wrap:anywhere]"><Linkified text={it.brief} /></p>}
            {it.link && (
              <a href={it.link} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="flex min-w-0 items-center gap-1.5 text-xs text-publish-fg underline underline-offset-2">
                <Link className="size-3 shrink-0" />
                <span className="truncate">{it.link}</span>
              </a>
            )}
            {it.files.length > 0 && <FileList files={it.files} download={download} />}
          </li>
        ))}
      </ol>
    </ModalSection>
  );
}

/** Формат для карточки фактов: у списка — все разные форматы через запятую */
export const formatsOf = (task: Task) => [...new Set(taskItems(task).map((i) => i.format))].join(", ");
